import React from 'react';
import {
  bool,
  elementType,
  func,
  string
} from 'prop-types';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { localizedType, surveyOptionsType } from '../../constants/types';
import { updateSurveyOptionsAction, updateSurveySchemaOptionsAction } from '../../actions';
import styles from './emailMessageInput.module.css';
import { LocalizedInput, LocalizedRichTextArea, LocalizedTextArea } from '../LocalizedInput';
import { AVAILABLE_LANGUAGES } from '../../constants';

const Title = ({ label, onClear, onReset }) => (
  <h3 className={styles.titleWithButton}>
    <span>{label}</span>
    {onClear && (
    <button type="button" className="button" onClick={onClear}>
      <Translate id="common.clear" />
    </button>
    )}
    {onReset && (
    <button type="button" className="button" onClick={onReset}>
      <Translate id="startSurvey.email.setDefaultContent" />
    </button>
    )}
  </h3>
);

Title.propTypes = {
  label: elementType.isRequired,
  onClear: func,
  onReset: func
};

Title.defaultProps = {
  onClear: undefined,
  onReset: undefined
};

class EmailMessageInput extends React.Component {
  constructor(props) {
    super(props);
    this.onClearClicked = this.onClearClicked.bind(this);
    this.onResetClicked = this.onResetClicked.bind(this);
    this.onLocalizedInputChange = this.onLocalizedInputChange.bind(this);
    this.onLocalizedRTEInputChange = this.onLocalizedRTEInputChange.bind(this);
    this.updateValues = this.updateValues.bind(this);

    this.state = { };
  }

  componentDidMount() {
    const {
      messageTitleName: titleName, messageContentName: contentName,
      defaultTitle, defaultContent, surveyOptions
    } = this.props;

    const hasTitle = surveyOptions[titleName]
      && Object.values(surveyOptions[titleName]).some((value) => typeof value === 'string');
    const hasContent = surveyOptions[contentName]
      && Object.values(surveyOptions[contentName]).some((value) => typeof value === 'string');

    if (hasTitle && hasContent) {
      this.props.dispatchUpdate();
    }

    const defaults = {};
    if (!hasTitle) {
      defaults[titleName] = defaultTitle || {};
    }
    if (!hasContent) {
      defaults[contentName] = defaultContent || {};
    }
    this.updateValues(defaults);
  }

  onClearClicked(name) {
    const newValue = AVAILABLE_LANGUAGES.reduce((acc, lang) => Object.assign(acc, { [lang]: '' }), {});
    return () => this.updateValues({ [name]: { ...newValue } });
  }

  onResetClicked(name, defaults) {
    return () => this.updateValues({ [name]: defaults });
  }

  onLocalizedInputChange(lang) {
    return (event) =>
      this.props.dispatchUpdate({
        [event.target.name]: { [lang]: event.target.value }
      });
  }

  onLocalizedRTEInputChange(lang, name) {
    return (value) =>
      this.props.dispatchUpdate({ [name]: { [lang]: value } });
  }

  updateValues(values) {
    this.setState(values);
    this.props.dispatchUpdate(values);
  }

  render() {
    const {
      surveyOptions, messageTitleName, messageContentName, defaultTitle, defaultContent, isRichText, customContentLabel, dataCyPrefix
    } = this.props;
    const {
      [messageContentName]: externallyChangedValues
    } = this.state;

    const messageTitleClasses = this.props.hasInvalidField(messageTitleName)
      ? `${styles.formItem} formError` : `${styles.formItem}`;
    const messageContentClasses = this.props.hasInvalidField(messageContentName)
      ? `${styles.formItem} formError` : `${styles.formItem}`;

    return (
      <div>
        {messageTitleName && (
          <div className={messageTitleClasses}>
            <Title
              label={<Translate id="startSurvey.email.emailTitle" />}
              onClear={defaultTitle && (() => this.onClearClicked(messageTitleName))}
              onReset={defaultTitle && (() => this.onResetClicked(messageTitleName, defaultTitle))}
              dataCyPrefix={`${dataCyPrefix}-title`}
            />
            <LocalizedInput
              name={messageTitleName}
              onInputChange={this.onLocalizedInputChange}
              values={surveyOptions[messageTitleName] || {}}
              placeholderTranslationNamespace="inLanguage"
              useAccordion
              maxLength={80}
              dataCyPrefix={`${dataCyPrefix}-title`}
            />
          </div>
        )}
        <div className={messageContentClasses}>
          <Title
            label={customContentLabel || <Translate id="startSurvey.email.emailContent" />}
            onClear={defaultContent && this.onClearClicked(messageContentName)}
            onReset={defaultContent && this.onResetClicked(messageContentName, defaultContent)}
            dataCyPrefix={`${dataCyPrefix}-content`}
          />
          {isRichText ? (
            <LocalizedRichTextArea
              name={messageContentName}
              onInputChange={this.onLocalizedRTEInputChange}
              values={surveyOptions[messageContentName] || {}}
              placeholderTranslationNamespace="inLanguage"
              externallyChangedValues={externallyChangedValues || {}}
              useAccordion
              dataCyPrefix={`${dataCyPrefix}-content`}
            />
          ) : (
            <LocalizedTextArea
              name={messageContentName}
              onInputChange={this.onLocalizedInputChange}
              values={surveyOptions[messageContentName] || {}}
              placeholderTranslationNamespace="inLanguage"
              useAccordion
              dataCyPrefix={`${dataCyPrefix}-content`}
            />
          )}
        </div>
      </div>
    );
  }
}

EmailMessageInput.propTypes = {
  surveyOptions: surveyOptionsType.isRequired,
  dispatchUpdate: func.isRequired,
  hasInvalidField: func,
  messageTitleName: string,
  messageContentName: string.isRequired,
  defaultTitle: localizedType,
  defaultContent: localizedType,
  isRichText: bool,
  customContentLabel: string,
  // eslint-disable-next-line react/no-unused-prop-types
  isSchema: bool,
  dataCyPrefix: string
};

EmailMessageInput.defaultProps = {
  hasInvalidField: () => {},
  messageTitleName: undefined,
  defaultTitle: undefined,
  defaultContent: undefined,
  isRichText: false,
  customContentLabel: undefined,
  isSchema: false,
  dataCyPrefix: undefined
};

const mapStateToProps = ({ survey, surveySchema }, ownProps) =>
  ({
    surveyOptions: ownProps.isSchema ? surveySchema.surveySchemaOptions : survey.surveyOptions
  });

const mapDispatchToProps = (dispatch, ownProps) => (
  {
    dispatchUpdate: (surveyOptions) =>
      dispatch(ownProps.isSchema
        ? updateSurveySchemaOptionsAction(surveyOptions)
        : updateSurveyOptionsAction(surveyOptions))
  }
);

export default connect(mapStateToProps, mapDispatchToProps)(EmailMessageInput);
